import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Radio, Send, ArrowLeft, Circle, MessageSquare, Trash2, Youtube, Wifi,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Studio() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const lastTsRef = useRef(null);

  const [liveVideo, setLiveVideo] = useState("");
  const [liveTitle, setLiveTitle] = useState("");
  const [liveBlurb, setLiveBlurb] = useState("");
  const [isLiveOn, setIsLiveOn] = useState(false);
  const [savingLive, setSavingLive] = useState(false);

  const [messages, setMessages] = useState([]);
  const [chatName, setChatName] = useState("Host");
  const [chatText, setChatText] = useState("");
  const chatEndRef = useRef(null);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [loading, user, navigate]);

  useEffect(() => {
    api.get("/live").then((r) => {
      const d = r.data || {};
      setLiveVideo(d.youtube_video_id || "");
      setLiveTitle(d.title || "");
      setLiveBlurb(d.blurb || "");
      setIsLiveOn(!!d.is_live);
    }).catch(() => {});
  }, []);

  const extractVideoId = (v) => {
    if (!v) return "";
    const m = v.match(/(?:v=|youtu\.be\/|live\/|embed\/|shorts\/)([\w-]{11})/);
    return m ? m[1] : v.trim();
  };

  const saveLive = async (goLiveNow) => {
    const vid = extractVideoId(liveVideo);
    if (goLiveNow && !vid) { toast.error("Paste your YouTube live link or video ID first"); return; }
    setSavingLive(true);
    try {
      await api.put("/admin/live", { is_live: goLiveNow, youtube_video_id: vid, title: liveTitle, blurb: liveBlurb });
      setLiveVideo(vid);
      setIsLiveOn(goLiveNow);
      toast.success(goLiveNow ? "You're LIVE on the site!" : "Stream stopped — site set to offline");
    } catch {
      toast.error("Couldn't update live status");
    } finally {
      setSavingLive(false);
    }
  };

  const loadMessages = useCallback(async () => {
    try {
      const res = await api.get(`/studio/messages`, { params: { room: "main", after: lastTsRef.current || undefined } });
      if (res.data.length) {
        lastTsRef.current = res.data[res.data.length - 1].created_at;
        setMessages((m) => {
          const seen = new Set(m.map((x) => x.id));
          const fresh = res.data.filter((x) => !seen.has(x.id));
          return fresh.length ? [...m, ...fresh] : m;
        });
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    loadMessages();
    const t = setInterval(loadMessages, 3000);
    return () => clearInterval(t);
  }, [loadMessages]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!chatText.trim()) return;
    try {
      await api.post("/studio/messages", { room: "main", name: chatName || "Host", text: chatText, kind: "host" });
      setChatText("");
    } catch { toast.error("Couldn't send message"); }
  };

  const clearChat = async () => {
    try {
      await api.delete("/studio/messages", { params: { room: "main" } });
      setMessages([]); lastTsRef.current = null;
      toast.success("Q&A cleared");
    } catch { toast.error("Couldn't clear"); }
  };

  if (loading || !user) {
    return <div className="min-h-screen bg-[#050B14] flex items-center justify-center"><Radio className="h-8 w-8 text-[#0066FF] animate-spin" /></div>;
  }

  const previewId = extractVideoId(liveVideo);

  return (
    <div className="min-h-screen bg-[#050B14] text-[#F5F7FA]" data-testid="studio-page">
      {/* Top bar */}
      <header className="border-b border-[#0066FF]/20 bg-[#0C1424] px-5 sm:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/admin" className="flex items-center gap-1.5 text-[#94A3B8] hover:text-[#0066FF] font-cond uppercase text-sm" data-testid="studio-back">
            <ArrowLeft className="h-4 w-4" /> Admin
          </Link>
          <span className="hidden sm:flex items-center gap-2 font-display text-lg tracking-wide">
            <Radio className="h-5 w-5 text-[#0066FF]" /> LIVE STUDIO
          </span>
        </div>
        <span className="font-mono2 text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full" style={isLiveOn ? { background: "rgba(169,21,32,0.2)", color: "#ff6b6b", border: "1px solid rgba(169,21,32,0.6)" } : { background: "#121D33", color: "#94A3B8" }} data-testid="studio-status">
          {isLiveOn ? "● On Air" : "Offline"}
        </span>
      </header>

      <div className="grid lg:grid-cols-4 gap-5 p-5 sm:p-6">
        {/* Broadcast column */}
        <div className="lg:col-span-3 space-y-4">
          {/* YouTube Live control */}
          <div className="rounded-2xl border border-[#FF0000]/30 bg-[#0C1424] p-5" data-testid="studio-youtube-live-panel">
            <div className="flex items-center justify-between gap-3 mb-3">
              <span className="flex items-center gap-2 font-cond font-bold uppercase text-sm"><Youtube className="h-5 w-5 text-[#FF0000]" /> YouTube Live Broadcast</span>
              <span className="font-mono2 text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full" style={isLiveOn ? { background: "rgba(169,21,32,0.2)", color: "#ff6b6b", border: "1px solid rgba(169,21,32,0.6)" } : { background: "#121D33", color: "#94A3B8" }}>{isLiveOn ? "● On Air" : "Offline"}</span>
            </div>
            <p className="text-xs text-[#94A3B8] mb-3">Go live in OBS/StreamYard → YouTube, then paste your YouTube live link here and press Go Live. Viewers watch on the site's Watch Live section with audience chat.</p>
            <div className="space-y-3">
              <input value={liveVideo} onChange={(e) => setLiveVideo(e.target.value)} placeholder="Paste YouTube live link or video ID" data-testid="studio-live-video-input" className="w-full rounded-lg bg-[#050B14] border border-[#0066FF]/25 px-3 py-2.5 text-sm outline-none focus:border-[#0066FF]" />
              <div className="grid sm:grid-cols-2 gap-3">
                <input value={liveTitle} onChange={(e) => setLiveTitle(e.target.value)} placeholder="Episode title (shown to viewers)" data-testid="studio-live-title-input" className="w-full rounded-lg bg-[#050B14] border border-[#0066FF]/25 px-3 py-2.5 text-sm outline-none focus:border-[#0066FF]" />
                <input value={liveBlurb} onChange={(e) => setLiveBlurb(e.target.value)} placeholder="Short description" className="w-full rounded-lg bg-[#050B14] border border-[#0066FF]/25 px-3 py-2.5 text-sm outline-none focus:border-[#0066FF]" />
              </div>
              <div className="flex flex-wrap gap-3">
                {!isLiveOn ? (
                  <button onClick={() => saveLive(true)} disabled={savingLive} data-testid="studio-publish-live-button" className="flex items-center gap-2 rounded-full bg-[#a91520] hover:bg-[#c11a27] disabled:opacity-60 transition-colors px-6 py-2.5 font-cond font-bold uppercase text-sm text-white">
                    <Circle className="h-4 w-4 fill-white" /> Go Live on Site
                  </button>
                ) : (
                  <button onClick={() => saveLive(false)} disabled={savingLive} data-testid="studio-stop-live-button" className="flex items-center gap-2 rounded-full bg-white text-[#050B14] hover:bg-[#e2e8f0] disabled:opacity-60 transition-colors px-6 py-2.5 font-cond font-bold uppercase text-sm">
                    Stop / Set Offline
                  </button>
                )}
                <button onClick={() => saveLive(isLiveOn)} disabled={savingLive} className="rounded-full border border-[#0066FF]/40 text-[#94A3B8] hover:text-white hover:border-[#0066FF] transition-colors px-5 py-2.5 font-cond font-bold uppercase text-sm">Save Details</button>
              </div>
            </div>
          </div>

          {/* Live preview */}
          <div className="relative rounded-2xl overflow-hidden border border-[#0066FF]/25 bg-black" style={{ paddingBottom: "56.25%" }} data-testid="studio-live-preview">
            {isLiveOn && previewId ? (
              <iframe
                title="Live preview"
                src={`https://www.youtube.com/embed/${previewId}`}
                allow="accelerometer; autoplay; encrypted-media; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full border-0"
                data-testid="studio-preview-iframe"
              />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-[#94A3B8] text-center px-6">
                <Youtube className="h-10 w-10" />
                <div className="font-cond uppercase text-sm">Not live yet</div>
                <div className="text-xs max-w-sm">Start your broadcast in OBS/StreamYard, paste the YouTube link above, and press <span className="text-white">Go Live on Site</span>. Your live program appears here and on the public Watch Live page.</div>
              </div>
            )}
          </div>
        </div>

        {/* On-site Q&A panel */}
        <div className="lg:col-span-1 rounded-2xl border border-[#0066FF]/25 bg-[#0C1424] flex flex-col h-[600px]" data-testid="studio-qa-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#0066FF]/15">
            <span className="flex items-center gap-2 font-cond font-bold uppercase text-sm"><MessageSquare className="h-4 w-4 text-[#0066FF]" /> On-Site Q&amp;A</span>
            <button onClick={clearChat} data-testid="studio-clear-chat" className="text-[#94A3B8] hover:text-[#FF3B30]" title="Clear Q&A"><Trash2 className="h-4 w-4" /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="studio-messages">
            {messages.length === 0 && <div className="text-center text-[#94A3B8] font-mono2 uppercase text-xs py-8">No questions yet</div>}
            {messages.map((m) => (
              <div key={m.id} className="rounded-lg bg-[#121D33] p-3" data-testid="studio-message">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-cond font-bold uppercase text-xs" style={{ color: m.kind === "host" ? "#FF6B00" : "#0066FF" }}>{m.name}</span>
                  {m.kind === "host" && <span className="font-mono2 text-[9px] uppercase text-[#FF6B00]">Host</span>}
                </div>
                <p className="text-sm text-[#CBD5E1]">{m.text}</p>
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>
          <form onSubmit={sendMessage} className="border-t border-[#0066FF]/15 p-3 space-y-2" data-testid="studio-chat-form">
            <input value={chatName} onChange={(e) => setChatName(e.target.value)} placeholder="Your name" className="w-full rounded-lg bg-[#050B14] border border-[#0066FF]/25 px-3 py-2 text-xs outline-none focus:border-[#0066FF]" />
            <div className="flex gap-2">
              <input value={chatText} onChange={(e) => setChatText(e.target.value)} placeholder="Ask or reply..." data-testid="studio-chat-input" className="flex-1 rounded-lg bg-[#050B14] border border-[#0066FF]/25 px-3 py-2 text-sm outline-none focus:border-[#0066FF]" />
              <button type="submit" data-testid="studio-chat-send" className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#0066FF] hover:bg-[#2B82FF] transition-colors"><Send className="h-4 w-4 text-white" /></button>
            </div>
          </form>
        </div>
      </div>

      <div className="px-5 sm:px-6 pb-8">
        <div className="rounded-xl border border-[#0066FF]/15 bg-[#0C1424] p-4 flex items-start gap-3 text-sm text-[#94A3B8]">
          <Wifi className="h-5 w-5 text-[#FF6B00] shrink-0 mt-0.5" />
          <span><span className="text-white font-semibold">How it works:</span> Compose your split-screen, logo and lower-thirds in OBS or StreamYard, stream to YouTube, then paste the link above and press Go Live. Audience chat happens on the public Watch Live page via YouTube; use this panel for internal question notes.</span>
        </div>
      </div>
    </div>
  );
}
